<!--
  Draws on a captured screenshot: boxes, arrows, highlights and text, with
  the code areas blurred or not. The canvas shows exactly the image a report
  sends: `onchange` hands over the composited canvas after every change.
-->
<script lang="ts">
  import { Annotations, draw, normalize, type Point, type Shape, type Tool } from '../feedback/annotate';
  import { blurAreas, type Capture } from '../feedback/capture';

  interface Props {
    capture: Capture;
    /** Whether the code areas are blurred. */
    blurCode: boolean;
    onchange: (composite: HTMLCanvasElement) => void;
  }
  let { capture, blurCode = $bindable(), onchange }: Props = $props();

  const tools: { id: Tool; label: string; hint: string }[] = [
    { id: 'box', label: 'Box', hint: 'Drag to draw a box' },
    { id: 'arrow', label: 'Arrow', hint: 'Drag from the tail to the tip' },
    { id: 'highlight', label: 'Highlight', hint: 'Drag over what to highlight' },
    { id: 'redact', label: 'Redact', hint: 'Drag over what to cover' },
    { id: 'text', label: 'Text', hint: 'Click where the note goes' },
  ];
  let tool: Tool = $state('box');
  const notes = new Annotations();
  let shapes: Shape[] = $state([]);
  let draft: Shape | null = $state(null);
  let start: Point | null = null;
  let canvas: HTMLCanvasElement | undefined = $state();
  /** A text note being typed: where, in capture pixels and on screen. */
  let typing: { at: Point; screen: { left: number; top: number }; text: string } | null = $state(null);

  function paint() {
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(capture.canvas, 0, 0);
    if (blurCode) blurAreas(ctx, capture.codeAreas, capture.scale);
    ctx.setTransform(capture.scale, 0, 0, capture.scale, 0, 0);
    draw(ctx, draft ? [...shapes, draft] : shapes);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  $effect(() => {
    // Repaint on every change of what is drawn; hand over the result once a drag ends.
    void shapes;
    void blurCode;
    void draft;
    paint();
    if (!draft && canvas) onchange(canvas);
  });

  /** The pointer's place in capture (CSS) pixels. */
  function at(e: PointerEvent): Point {
    const r = canvas!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * capture.width, y: ((e.clientY - r.top) / r.height) * capture.height };
  }

  function shape(a: Point, b: Point): Shape {
    if (tool === 'arrow') return { kind: 'arrow', from: a, to: b };
    return { kind: tool === 'highlight' || tool === 'redact' ? tool : 'box', ...normalize(a, b) };
  }

  function down(e: PointerEvent) {
    if (typing) commitText();
    if (tool === 'text') {
      const r = canvas!.getBoundingClientRect();
      typing = { at: at(e), screen: { left: e.clientX - r.left, top: e.clientY - r.top }, text: '' };
      e.preventDefault();
      return;
    }
    start = at(e);
    canvas!.setPointerCapture(e.pointerId);
  }
  function move(e: PointerEvent) {
    if (start) draft = shape(start, at(e));
  }
  function up(e: PointerEvent) {
    if (!start) return;
    notes.add(shape(start, at(e)));
    start = null;
    draft = null;
    shapes = notes.shapes;
  }
  function commitText() {
    if (!typing) return;
    notes.add({ kind: 'text', x: typing.at.x, y: typing.at.y, text: typing.text });
    typing = null;
    shapes = notes.shapes;
  }
  function undo() {
    notes.undo();
    shapes = notes.shapes;
  }
  function clear() {
    notes.clear();
    shapes = notes.shapes;
  }
</script>

<div class="annotator">
  <div class="tools" role="toolbar" aria-label="Annotate the screenshot">
    {#each tools as t (t.id)}
      <button class="btn small" class:active={tool === t.id} aria-pressed={tool === t.id} title={t.hint} onclick={() => (tool = t.id)}>{t.label}</button>
    {/each}
    <span class="sep" aria-hidden="true"></span>
    <button class="btn small" onclick={undo} disabled={!shapes.length}>Undo</button>
    <button class="btn small" onclick={clear} disabled={!shapes.length}>Clear</button>
    <span class="sep" aria-hidden="true"></span>
    <button
      class="btn small blur"
      class:active={blurCode && capture.codeAreas.length > 0}
      aria-pressed={blurCode && capture.codeAreas.length > 0}
      disabled={!capture.codeAreas.length}
      title={capture.codeAreas.length ? 'Blur the diff and code areas, so no code leaves your browser' : 'No code or diff area is in view'}
      onclick={() => (blurCode = !blurCode)}
      >{!capture.codeAreas.length ? 'No code in view' : blurCode ? `Code blurred (${capture.codeAreas.length})` : `Blur code (${capture.codeAreas.length})`}</button
    >
  </div>
  <p class="hint">Only code is blurred, and typed text is left out: cover anything else with Redact.</p>
  <div class="stage">
    <canvas
      bind:this={canvas}
      width={capture.canvas.width}
      height={capture.canvas.height}
      class:text={tool === 'text'}
      aria-label="The screenshot, as it will be sent. {tools.find((t) => t.id === tool)?.hint}."
      onpointerdown={down}
      onpointermove={move}
      onpointerup={up}
      onpointercancel={() => {
        start = null;
        draft = null;
      }}
    ></canvas>
    {#if typing}
      <input
        class="note-input"
        style="left:{typing.screen.left}px;top:{typing.screen.top}px"
        aria-label="Text to place on the screenshot"
        placeholder="Type, then Enter"
        maxlength="120"
        {@attach (node: HTMLInputElement) => {
          node.focus();
          // Again after the pointer's own focus handling, should it have moved the focus.
          requestAnimationFrame(() => {
            if (document.activeElement !== node) node.focus();
          });
        }}
        bind:value={typing.text}
        onkeydown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitText();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            typing = null;
          }
        }}
        onblur={commitText}
      />
    {/if}
  </div>
</div>

<style>
  .annotator {
    display: grid;
    gap: 8px;
  }
  .tools {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .sep {
    width: 1px;
    align-self: stretch;
    background: var(--line);
    margin: 0 2px;
  }
  .blur.active {
    background: var(--good);
    border-color: var(--good);
    color: var(--surface);
  }
  .hint {
    margin: 0;
    font-size: 12px;
    color: var(--muted);
  }
  .stage {
    position: relative;
    border: 1px solid var(--line);
    border-radius: 8px;
    overflow: hidden;
    background: var(--code);
  }
  canvas {
    display: block;
    width: 100%;
    height: auto;
    cursor: crosshair;
    touch-action: none;
  }
  canvas.text {
    cursor: text;
  }
  .note-input {
    position: absolute;
    transform: translateY(-100%);
    width: min(240px, 70%);
    padding: 4px 6px;
    font-size: 13px;
    border: 2px solid #e5484d;
  }
</style>
